import { DestroyRef, Injectable, inject, signal } from '@angular/core';

/**
 * Easter-egg trigger hub.
 *
 * Triggers (any of):
 *  - Konami code: ↑↑↓↓←→←→ B A
 *  - Programmatic: `trigger()` (e.g. footer heart double-click)
 *
 * Owns the `walking` + `highFive` + `footprints` state and a 10-second
 * choreography. The PenguinOverlayComponent renders the visuals; this
 * service exposes only signals and a `trigger()` API.
 */
@Injectable({ providedIn: 'root' })
export class PenguinService {
  /** True while the two penguins are walking on screen. */
  readonly walking = signal(false);

  /** True for ~1.2s mid-animation when the two penguins meet. */
  readonly highFive = signal(false);

  /** Freezes the walk (not the waddle) so the penguins pose during the greeting. */
  readonly paused = signal(false);

  /** Drives the "Hello, I'm AMIE!" speech bubble — held for 10s after the meet-up. */
  readonly greeting = signal(false);

  /** Screen-coordinate footprint trail; capped at 20. */
  readonly footprints = signal<{ id: number; x: number; y: number }[]>([]);

  private konamiSequence: readonly string[] = [
    'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
    'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight',
    'b', 'a',
  ];
  private konamiIndex = 0;
  private footprintId = 0;
  private footprintInterval?: ReturnType<typeof setInterval>;

  constructor() {
    if (typeof document === 'undefined') return;
    document.addEventListener('keydown', this.onKeydown);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.footprintInterval) clearInterval(this.footprintInterval);
    });
  }

  /**
   * Public entry point — also invoked by the footer heart double-click.
   *
   * Choreography (matches the CSS keyframes in PenguinOverlayComponent):
   *   0s      start walking inward from the edges
   *   ~4.2s   arrive at horizontal center, slide vertically toward each other
   *   5.4s    meet — high-five burst plays for 1.4s; walk freezes, greeting shows
   *   5.4–7.4s  penguins hold the pose (still waddling), greeting bubbles
   *   7.4s    resume walking out the other side
   *   ~14s    leave viewport
   *
   * The walk is a single 12s CSS animation; the 2s pause is applied via
   * `animation-play-state: paused` (the `paused` signal), so wall-clock end is
   * 12s + 2s = ~14s.
   */
  private readonly walkMs = 12000;
  private readonly meetMs = 5400;
  private readonly greetHoldMs = 2000;

  trigger(): void {
    if (this.walking()) return;
    this.walking.set(true);
    this.footprints.set([]);
    this.playNootNoot();

    this.footprintInterval = setInterval(() => this.spawnFootprints(), 300);

    // ~5.4s in: penguins meet at center after the vertical approach.
    setTimeout(() => {
      this.highFive.set(true);
      this.playNootNoot();
      setTimeout(() => this.highFive.set(false), 1400);

      // Freeze the walk + greet. Stop spawning footprints while the feet aren't moving.
      this.paused.set(true);
      this.greeting.set(true);
      if (this.footprintInterval) clearInterval(this.footprintInterval);

      setTimeout(() => {
        this.greeting.set(false);
        this.paused.set(false);
        this.footprintInterval = setInterval(() => this.spawnFootprints(), 300);
      }, this.greetHoldMs);
    }, this.meetMs);

    // Full walk + the held pause; +2s footprint fade.
    setTimeout(() => {
      this.walking.set(false);
      if (this.footprintInterval) clearInterval(this.footprintInterval);
      setTimeout(() => this.footprints.set([]), 2000);
    }, this.walkMs + this.greetHoldMs + 500);
  }

  /** Footer heart and penguin click handler — plays a quick noot. */
  noot(): void {
    this.playNootNoot();
  }

  private onKeydown = (event: KeyboardEvent): void => {
    // Konami code.
    if (event.key === this.konamiSequence[this.konamiIndex]) {
      this.konamiIndex++;
      if (this.konamiIndex === this.konamiSequence.length) {
        this.konamiIndex = 0;
        this.trigger();
      }
    } else {
      this.konamiIndex = 0;
    }
  };

  private spawnFootprints(): void {
    const topEl = document.querySelector('.penguin-top') as HTMLElement | null;
    const bottomEl = document.querySelector('.penguin-bottom') as HTMLElement | null;
    const newPrints: { id: number; x: number; y: number }[] = [];

    /*
     * Trail position rules — footprints belong BEHIND the direction of travel and AT
     * the feet. The previous code put bottom-penguin prints at the wrong x (left edge
     * of the bbox while it walks left → that's IN FRONT of the walker) and at the wrong
     * y (above the body instead of at floor level). Both fixed below.
     *
     * The `<span>` pair inside .footprint renders as a 14px-wide block, so we offset
     * the spawn point by half the width to keep the trail centered under the walker.
     */
    if (topEl) {
      const r = topEl.getBoundingClientRect();
      // Top walker moves L→R; trail at the LEFT (rear) edge, just below the feet.
      newPrints.push({ id: this.footprintId++, x: r.left + 18, y: r.bottom - 4 });
    }
    if (bottomEl) {
      const r = bottomEl.getBoundingClientRect();
      // Bottom walker moves R→L; trail at the RIGHT (rear) edge, also at feet level.
      newPrints.push({ id: this.footprintId++, x: r.right - 32, y: r.bottom - 4 });
    }

    this.footprints.update((fp) => {
      const next = [...fp, ...newPrints];
      return next.length > 20 ? next.slice(next.length - 20) : next;
    });
  }

  /** Two-toned chirp via WebAudio — no asset bundling required. */
  private playNootNoot(): void {
    try {
      const ctx = new AudioContext();
      const playNoot = (delay: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.setValueAtTime(400, ctx.currentTime + delay);
        osc.frequency.linearRampToValueAtTime(300, ctx.currentTime + delay + 0.15);
        gain.gain.setValueAtTime(0.15, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + 0.15);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.15);
      };
      playNoot(0);
      playNoot(0.2);
    } catch {
      // AudioContext blocked (e.g. before any user gesture). Silent fail.
    }
  }
}
